import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

// Parses the URL hash Supabase appends after invite/recovery/magic-link.
// Handles three shapes:
//   1) #access_token=...&refresh_token=...&type=invite|recovery|magiclink  (implicit flow, default)
//   2) ?code=...                                                            (PKCE flow)
//   3) #error=...&error_description=...                                     (expired/consumed)
function parseAuthFragment() {
  const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
  const hashParams = new URLSearchParams(hash);
  const query = new URLSearchParams(window.location.search);
  return {
    accessToken: hashParams.get('access_token'),
    refreshToken: hashParams.get('refresh_token'),
    type: hashParams.get('type') || query.get('type'),
    errorDescription: hashParams.get('error_description') || query.get('error_description'),
    code: query.get('code'),
  };
}

export default function AuthCallback() {
  const nav = useNavigate();
  const { recoveryPending, clearRecoveryPending, clearSessionAndLogout } = useAuth();
  // Read the pre-React snapshot written by the inline script in index.html.
  const earlySnapshot = (() => {
    try {
      const pending = sessionStorage.getItem('velvet-recovery-pending') === '1';
      const type = sessionStorage.getItem('velvet-auth-type');
      const err = sessionStorage.getItem('velvet-auth-error');
      return { pending, type, err };
    } catch { return { pending: false, type: null, err: null }; }
  })();
  const [phase, setPhase] = useState(() => (earlySnapshot.err ? 'error' : 'verifying'));
  const [errorMsg, setErrorMsg] = useState(earlySnapshot.err || '');
  const [flowType, setFlowType] = useState(earlySnapshot.type || null);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { accessToken, refreshToken, type, errorDescription, code } = parseAuthFragment();
      // Fallback: read the pre-React snapshot written by index.html (survives
      // supabase-js clearing the hash and React-router navigation).
      let at = accessToken;
      let rt = refreshToken;
      try {
        if (!at) at = sessionStorage.getItem('velvet-auth-access-token');
        if (!rt) rt = sessionStorage.getItem('velvet-auth-refresh-token');
      } catch { /* ignore */ }

      if (errorDescription) {
        if (!cancelled) { setErrorMsg(decodeURIComponent(errorDescription)); setPhase('error'); }
        return;
      }

      try {
        if (at && rt) {
          const { error } = await supabase.auth.setSession({ access_token: at, refresh_token: rt });
          if (error) throw error;
          console.info('[auth-callback] setSession OK');
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        }
        // Remove tokens from address bar so they don't leak.
        window.history.replaceState({}, '', '/auth/callback');
        if (cancelled) return;

        // Priority order for deciding what to render:
        //   1. If Supabase told us this was a PASSWORD_RECOVERY event (or the early
        //      inline snapshot flagged one), always show setPassword.
        //   2. Else if the URL fragment carried type=invite|recovery, show setPassword.
        //   3. Else if we already have a session, we were opened during a normal login → send to dashboard.
        //   4. Else: error (link had no credentials and no session).
        const t = (type || earlySnapshot.type || '').toLowerCase();
        if (recoveryPending || earlySnapshot.pending || t === 'invite' || t === 'recovery') {
          setFlowType(t || 'recovery');
          setPhase('setPassword');
          return;
        }
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          setPhase('done');
          setTimeout(() => nav('/', { replace: true }), 300);
        } else {
          setErrorMsg('El enlace no incluye credenciales o ya expiró. Solicita un nuevo correo.');
          setPhase('error');
        }
      } catch (e) {
        if (!cancelled) { setErrorMsg(e?.message || 'No se pudo validar el enlace.'); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Mínimo 8 caracteres.');
    if (password !== password2) return toast.error('Las contraseñas no coinciden.');
    setSaving(true);
    console.info('[auth-callback] submit start');
    try {
      // Sanity-check we have an active recovery session BEFORE calling updateUser.
      const pre = await supabase.auth.getSession();
      console.info('[auth-callback] session before updateUser:',
        pre?.data?.session ? `user=${pre.data.session.user?.id}` : 'NULL');
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) { console.error('[auth-callback] updateUser error', error); throw error; }
      if (!data?.user) throw new Error('Supabase no confirmó la actualización.');
      console.info('[auth-callback] updateUser OK, user id:', data.user.id);
      // Only after Supabase confirms, mark done and clear the recovery flag.
      clearRecoveryPending();
      toast.success('Contraseña definida');
      // Sign the recovery session out so the user must sign in again with the new
      // password. This is the only way to prove end-to-end the password actually works.
      try { await supabase.auth.signOut(); } catch (_) { /* ignore */ }
      setPhase('done');
      setTimeout(() => {
        window.location.replace('/login');
      }, 600);
    } catch (err) {
      console.error('[auth-callback] submit failed', err);
      toast.error(err?.message || 'No se pudo guardar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="auth-callback-page">
      <div className="w-full max-w-sm space-y-5">
        <div>
          <div className="velvet-eyebrow mb-2">Velvet OS</div>
          <h1 className="font-serif text-3xl tracking-tight" data-testid="auth-callback-title">
            {phase === 'setPassword' ? 'Define tu contraseña' :
             phase === 'error' ? 'Enlace no válido' :
             phase === 'done' ? 'Todo listo' : 'Verificando enlace…'}
          </h1>
          {flowType === 'invite' && phase === 'setPassword' && (
            <p className="text-sm text-muted-foreground mt-2">
              Estás activando tu cuenta. Elige una contraseña que solo tú conozcas.
            </p>
          )}
          {flowType === 'recovery' && phase === 'setPassword' && (
            <p className="text-sm text-muted-foreground mt-2">
              Recuperación de contraseña. Ingresa la nueva contraseña. Al guardar cerraremos esta sesión temporal y te pediremos iniciar con la contraseña nueva.
            </p>
          )}
        </div>

        {phase === 'verifying' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="auth-callback-verifying">
            <Loader2 className="h-4 w-4 animate-spin" /> Validando token…
          </div>
        )}

        {phase === 'setPassword' && (
          <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
            <div>
              <Label>Nueva contraseña</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required autoFocus minLength={8} className="rounded-sm"
                     data-testid="set-password-input" />
            </div>
            <div>
              <Label>Confirmar contraseña</Label>
              <Input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)}
                     required minLength={8} className="rounded-sm"
                     data-testid="set-password-confirm" />
            </div>
            <Button type="submit" disabled={saving}
                    className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="set-password-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar y entrar'}
            </Button>
            <p className="text-xs text-muted-foreground">Mínimo 8 caracteres. No se comparte con nadie.</p>
          </form>
        )}

        {phase === 'error' && (
          <div className="space-y-4" data-testid="auth-callback-error">
            <p className="text-sm text-destructive">{errorMsg}</p>
            <Button variant="outline" onClick={() => { clearRecoveryPending(); clearSessionAndLogout(); }}
                    className="rounded-sm" data-testid="auth-callback-back-login">
              Volver a iniciar sesión
            </Button>
          </div>
        )}

        {phase === 'done' && (
          <p className="text-sm text-muted-foreground" data-testid="auth-callback-done">Redirigiendo al inicio de sesión…</p>
        )}
      </div>
    </div>
  );
}
